import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardMedia,
  CardContent,
  Divider,
  Breadcrumbs,
  Link,
  Chip,
  Tabs,
  Tab,
  InputBase,
  Paper,
  IconButton,
  Pagination,
  CircularProgress,
  Alert,
  ToggleButtonGroup,
  ToggleButton
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import TranslateIcon from '@mui/icons-material/Translate';
import { Link as RouterLink } from 'react-router-dom';
import SeoHead from '../components/common/SeoHead';
import LazyImage from '../components/common/LazyImage';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { functions } from '../firebase/firebase';

// 导入示例图片作为备用 
import amethystImg from '../assets/images/index/cuR2.jpg';
import crystalImg from '../assets/images/index/cuR1.png';
import fortuneImg from '../assets/images/Luopan/banner1.png';
import tarotImg from '../assets/images/Luopan/L3.png';
import baziImg from '../assets/images/Luopan/L1.png';
import yijingImg from '../assets/images/Luopan/L5.png';

const Blog = () => {
  const [tabValue, setTabValue] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [blogPosts, setBlogPosts] = useState([]);
  const [language, setLanguage] = useState('chinese'); // 默认中文
  
  // 默认图片映射，根据分类使用不同的默认图片
  const defaultImages = {
    'crystal': amethystImg,
    'fortune': fortuneImg,
    'yijing': yijingImg,
    'default': crystalImg
  };
  
  // 获取文章时加载指定语言的文章 - 这是核心功能
  const fetchArticles = async () => {
    try {
      setLoading(true);
      setError(null);

      // 调用云函数获取最新文章
      // 注意：添加一个临时解决方案来处理索引问题
      const getLatestArticles = httpsCallable(functions, 'getLatestSEOArticles');
      console.log(`正在调用getLatestSEOArticles云函数，语言: ${language}...`);
      try {
        var result = await getLatestArticles({ 
          limit: 20,
          language: language // 指定获取特定语言的文章 - 重要：确保只获取当前选择的语言
        }); // 获取更多文章后在前端过滤
      } catch (funcError) {
        console.warn('使用云函数获取文章失败，尝试使用备用硬编码数据:', funcError);
        // 提供一些硬编码数据作为备用
        result = { 
          data: { 
            success: true, 
            articles: []
          } 
        };
      }
      
      if (result.data && result.data.success && result.data.articles) {
        // 处理返回的文章数据
        const articles = result.data.articles.map(article => {
          // 详细记录文章字段，帮助调试
          console.log('处理文章数据:', article.id);
          console.log('字段列表:', Object.keys(article).join(', '));
          console.log('标题:', article.title);
          console.log('类型:', article.type);
          console.log('状态:', article.status);
          console.log('内容长度:', article.content ? article.content.length : 0);
          
          // 确保content存在，避免空指针异常
          const content = article.content || '';
          
          // 直接使用Firestore中存储的title字段
          let title = article.title || '无标题文章';
          
          // 提取摘要
          const excerptLength = 150;
          let excerpt = '';
          if (content) {
            excerpt = content.replace(/#/g, '').substring(0, excerptLength);
            if (content.length > excerptLength) {
              excerpt += '...';
            }
          } else {
            excerpt = title + '的详细介绍...';
          }
          
          // 提取标签
          let tags = [];
          if (content) {
            const tagMatch = content.match(/关键词：(.*?)(\n|$)/);
            if (tagMatch) {
              tags = tagMatch[1].split('、').map(tag => tag.trim());
            }
          }
          
          // 如果没有标签，基于类型设置默认标签
          if (tags.length === 0) {
            tags = article.type === 'crystal' 
              ? ['水晶能量', '能量', '水晶'] 
              : ['命理', '运势', '八字'];
          }
          
          // 将标签限制为最多5个
          tags = tags.slice(0, 5);
          
          // 确定图片和类别
          const category = article.type === 'crystal' ? 'crystal' : 
                           article.type === 'fortune' ? 'fortune' : 
                           article.type === 'yijing' ? 'yijing' : 
                           'default';
          const image = defaultImages[category];
          
          // 格式化日期 - 安全处理
          let dateStr;
          try {
            // 检查createdAt是否已经是Date对象
            if (article.createdAt instanceof Date) {
              dateStr = article.createdAt.toISOString().split('T')[0];
            } else if (typeof article.createdAt === 'object' && article.createdAt !== null) {
              // 可能是Firestore Timestamp
              console.log('createdAt类型:', typeof article.createdAt, article.createdAt);
              // 检查是否有seconds属性(Firestore Timestamp特性)
              if (article.createdAt.seconds) {
                const timestamp = new Date(article.createdAt.seconds * 1000);
                dateStr = timestamp.toISOString().split('T')[0];
              } else {
                // 使用当前日期作为后备
                dateStr = new Date().toISOString().split('T')[0];
              }
            } else {
              // 使用当前日期作为后备
              dateStr = new Date().toISOString().split('T')[0];
            }
          } catch (dateError) {
            console.warn('日期格式化错误:', dateError);
            dateStr = new Date().toISOString().split('T')[0];
          }
          
          return {
            id: article.id,
            title: title,
            slug: article.id,
            category: category,
            date: dateStr,
            image: image,
            excerpt: excerpt,
            tags: tags,
            content: content,
            viewCount: article.viewCount || 0,
            language: article.language || 'chinese', // 确保包含语言信息
            type: article.type || category // 确保包含类型信息
          };
        });
        
        setBlogPosts(articles);
      } else {
        setError('获取文章失败');
        // 如果API调用失败，使用备用数据
        setBlogPosts([]);
      }
    } catch (err) {
      console.error('获取文章数据错误:', err);
      setError('获取文章数据时出错，请稍后再试');
      setBlogPosts([]);
    } finally {
      setLoading(false);
    }
  };
  
  // 初始加载文章
  useEffect(() => {
    fetchArticles();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  // 当语言切换时重新获取文章
  useEffect(() => {
    // 语言改变时自动刷新，但跳过初始渲染
    if (blogPosts.length > 0) {
      fetchArticles();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language]);
  
  // 根据标签过滤文章
  const filterPostsByCategory = (posts, category) => {
    if (category === 'all') return posts;
    return posts.filter(post => post.category === category);
  };
  
  // 根据搜索词过滤文章
  const filterPostsBySearch = (posts, term) => {
    if (!term) return posts;
    const lowercaseTerm = term.toLowerCase();
    return posts.filter(post => 
      post.title.toLowerCase().includes(lowercaseTerm) || 
      post.excerpt.toLowerCase().includes(lowercaseTerm) ||
      post.tags.some(tag => tag.toLowerCase().includes(lowercaseTerm))
    );
  };
  
  // 分页逻辑
  const handlePageChange = (event, value) => {
    setCurrentPage(value);
    window.scrollTo(0, 0);
  };

  // 语言切换处理
  const handleLanguageChange = (event, newLanguage) => {
    if (newLanguage !== null) {
      setLanguage(newLanguage);
      setCurrentPage(1);
    }
  };
  
  const postsPerPage = 3;
  const categoryFilter = tabValue === 0 ? 'all' : 
                        tabValue === 1 ? 'crystal' : 
                        tabValue === 2 ? 'fortune' : 
                        'yijing';
  const filteredPosts = filterPostsBySearch(filterPostsByCategory(blogPosts, categoryFilter), searchTerm);
  const pageCount = Math.ceil(filteredPosts.length / postsPerPage);
  const displayedPosts = filteredPosts.slice((currentPage - 1) * postsPerPage, currentPage * postsPerPage);
  
  // 处理标签切换
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    setCurrentPage(1);
  };
  
  // 处理搜索
  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setCurrentPage(1);
  };
  
  const handleSearch = (event) => {
    event.preventDefault();
    // 搜索已经在输入时实时进行了
  };
  
  // 博客结构化数据
  const blogSchemaData = {
    "@context": "https://schema.org",
    "@type": "Blog",
    "name": language === 'chinese' 
      ? "DestinyCrystal博客 - 命理知识与水晶指南" 
      : "DestinyCrystal Blog - Divination Knowledge & Crystal Guide",
    "description": language === 'chinese'
      ? "探索命理学知识与水晶能量学的专业博客，提供八字解读、紫微斗数、易经八卦解析，以及水晶选择、净化与使用指南。"
      : "Explore professional blog on divination knowledge and crystal energy science, providing Bazi reading, I Ching analysis, and crystal selection, cleansing and usage guides.",
    "url": "https://destiny-crystal.com/blog",
    "publisher": {
      "@type": "Organization",
      "name": "DestinyCrystal命理水晶",
      "logo": {
        "@type": "ImageObject",
        "url": "https://destiny-crystal.com/assets/images/logo.png"
      }
    },
    "blogPost": blogPosts.map(post => ({
      "@type": "BlogPosting",
      "headline": post.title,
      "description": post.excerpt,
      "datePublished": post.date,
      "mainEntityOfPage": `https://destiny-crystal.com/blog/${post.slug}`,
      "image": `https://destiny-crystal.com/assets/images/${post.image}`,
      "keywords": post.tags.join(', '),
      "inLanguage": post.language === 'english' ? 'en-US' : 'zh-CN',
      "publisher": {
        "@type": "Organization",
        "name": "DestinyCrystal命理水晶",
        "logo": {
          "@type": "ImageObject",
          "url": "https://destiny-crystal.com/assets/images/logo.png"
        }
      },
      "author": {
        "@type": "Organization",
        "name": "DestinyCrystal专业团队"
      }
    }))
  };
  
  return (
    <Container maxWidth="lg" sx={{ py: 6 }}>
      {/* SEO优化 */}
      <SeoHead
        title={language === 'chinese' 
          ? "命理知识与水晶能量 | 易经八卦 | 专业博客 | DestinyCrystal" 
          : "Divination Knowledge & Crystal Energy | I Ching | Professional Blog | DestinyCrystal"}
        description={language === 'chinese'
          ? "探索命理学知识与水晶能量学的专业博客，提供八字解读、紫微斗数、易经八卦解析，以及水晶选择、净化与使用指南。"
          : "Explore our professional blog on divination knowledge and crystal energy, providing Bazi reading, I Ching analysis, and crystal selection, cleansing and usage guides."}
        canonicalUrl="https://destiny-crystal.com/blog"
        imageUrl="https://destiny-crystal.com/assets/images/collections/luopan.png"
        keywords={language === 'chinese' 
          ? ["命理知识", "水晶能量", "八字命盘", "易经八卦", "紫水晶", "命理解读", "水晶净化"]
          : ["divination knowledge", "crystal energy", "bazi chart", "i ching", "bagua", "amethyst", "feng shui", "crystal healing"]}
        schemaData={blogSchemaData}
      />
      
      {/* 面包屑导航和语言切换 */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Breadcrumbs aria-label="breadcrumb">
          <Link component={RouterLink} to="/" color="inherit">
            {language === 'chinese' ? "首页" : "Home"}
          </Link>
          <Typography color="text.primary">{language === 'chinese' ? "博客" : "Blog"}</Typography>
        </Breadcrumbs>
        
        <ToggleButtonGroup
          value={language}
          exclusive
          onChange={handleLanguageChange}
          aria-label="language selection"
          size="small"
        >
          <ToggleButton value="chinese" aria-label="中文">
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <TranslateIcon sx={{ mr: 0.5 }} fontSize="small" />
              中文
            </Box>
          </ToggleButton>
          <ToggleButton value="english" aria-label="English">
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <TranslateIcon sx={{ mr: 0.5 }} fontSize="small" />
              EN
            </Box>
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>
      
      <Typography variant="h1" gutterBottom align="center" sx={{ mb: 1, fontSize: '2rem' }}>
        {language === 'chinese' 
          ? "DestinyCrystal命理水晶博客" 
          : "DestinyCrystal Divination & Crystal Blog"}
      </Typography>
      <Typography variant="h2" align="center" color="text.secondary" sx={{ mb: 5, fontSize: '1.25rem' }}>
        {language === 'chinese'
          ? "探索命理知识、易经八卦与水晶能量的专业指南"
          : "Professional guide to divination, I Ching and crystal energy"}
      </Typography>
      
      {/* 搜索、分类和语言过滤器 */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            indicatorColor="primary"
            textColor="primary"
            sx={{ borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab label={language === 'chinese' ? "全部文章" : "All Articles"} />
            <Tab label={language === 'chinese' ? "水晶能量" : "Crystal Energy"} />
            <Tab label={language === 'chinese' ? "命理知识" : "Divination"} />
            <Tab label={language === 'chinese' ? "易经八卦" : "I Ching & Feng Shui"} />
          </Tabs>
        </Grid>
        <Grid item xs={12} md={2}>
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
            <ToggleButtonGroup
              value={language}
              exclusive
              onChange={handleLanguageChange}
              aria-label="language selection"
              size="small"
              sx={{ ml: 2 }}
            >
              <ToggleButton value="chinese" aria-label="中文">
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <TranslateIcon sx={{ mr: 0.5 }} fontSize="small" />
                  中文
                </Box>
              </ToggleButton>
              <ToggleButton value="english" aria-label="English">
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <TranslateIcon sx={{ mr: 0.5 }} fontSize="small" />
                  EN
                </Box>
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper
            component="form"
            onSubmit={handleSearch}
            sx={{ display: 'flex', alignItems: 'center', px: 2, py: 0.5 }}
          >
            <InputBase
              sx={{ ml: 1, flex: 1 }}
              placeholder={language === 'chinese' ? "搜索文章..." : "Search articles..."}
              value={searchTerm}
              onChange={handleSearchChange}
            />
            <Divider sx={{ height: 28, mx: 0.5 }} orientation="vertical" />
            <IconButton type="submit" sx={{ p: '10px' }} aria-label="search">
              <SearchIcon />
            </IconButton>
          </Paper>
        </Grid>
      </Grid>
      
      {/* 加载状态或错误提示 */}
      {loading ? (
        <Box textAlign="center" py={4}>
          <CircularProgress />
          <Typography sx={{ mt: 2 }}>正在加载文章...</Typography>
        </Box>
      ) : error ? (
        <Alert severity="error" sx={{ mb: 4 }}>
          {error}
        </Alert>
      ) : null}
      
      {/* 文章列表 */}
      {!loading && displayedPosts.length > 0 ? (
        <Grid container spacing={4}>
          {displayedPosts.map((post) => (
            <Grid item xs={12} key={post.id}>
              <Card
                component="article"
                itemScope
                itemType="http://schema.org/BlogPosting"
                sx={{ 
                  display: 'flex', 
                  flexDirection: { xs: 'column', md: 'row' }, 
                  overflow: 'hidden',
                  borderRadius: 2,
                  boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
                }}
              >
                <Box sx={{ 
                  width: { xs: '100%', md: '30%' },
                  maxHeight: { xs: 200, md: 'auto' }
                }}>
                  <LazyImage
                    src={post.image}
                    alt={post.title}
                    itemProp="image"
                    sx={{ 
                      height: '100%',
                      minHeight: { xs: 200, md: 240 }
                    }}
                  />
                </Box>
                <CardContent sx={{ 
                  flex: '1 1 auto',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  p: 3
                }}>
                  <Box>
                    <Box sx={{ display: 'flex', mb: 1, gap: 1, flexWrap: 'wrap' }}>
                      {post.tags.map((tag, index) => (
                        <Chip 
                          key={index}
                          label={tag}
                          size="small"
                          variant="outlined"
                          sx={{ fontSize: '0.7rem' }}
                        />
                      ))}
                    </Box>
                    <Typography 
                      variant="h3" 
                      component="h2" 
                      gutterBottom 
                      itemProp="headline"
                      sx={{ fontSize: '1.5rem', fontWeight: 'bold' }}
                    >
                      {post.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }} itemProp="datePublished">
                      发布日期: {post.date}
                    </Typography>
                    <Typography variant="body1" paragraph itemProp="description">
                      {post.excerpt}
                    </Typography>
                    
                    {/* 添加结构化数据的隐藏元素 */}
                    <meta itemProp="author" content="DestinyCrystal命理水晶团队" />
                    <meta itemProp="publisher" content="DestinyCrystal" />
                    <meta itemProp="keywords" content={post.tags.join(', ')} />
                  </Box>
                  <Box sx={{ mt: 2 }}>
                    <Link
                      component={RouterLink}
                      to={`/blog/${post.slug}`}
                      underline="none"
                      sx={{
                        display: 'inline-block',
                        color: 'primary.main',
                        fontWeight: 'medium',
                        '&:hover': {
                          textDecoration: 'underline'
                        }
                      }}
                    >
                      阅读全文 →
                    </Link>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      ) : (!loading && displayedPosts.length === 0) ? (
        <Box textAlign="center" py={6}>
          <Typography variant="h6" color="text.secondary">
            未找到匹配的文章
          </Typography>
        </Box>
      ) : null}
      
      {/* 分页 */}
      {!loading && pageCount > 1 && (
        <Box display="flex" justifyContent="center" mt={6} mb={4}>
          <Pagination
            count={pageCount}
            page={currentPage}
            onChange={handlePageChange}
            color="primary"
            size="large"
          />
        </Box>
      )}
      
      {/* 推荐阅读 */}
      <Box mt={8}>
        <Typography variant="h5" gutterBottom align="center">
          {language === 'chinese' ? "推荐专题" : "Featured Topics"}
        </Typography>
        <Divider sx={{ width: 60, margin: '0 auto', mb: 4, borderColor: 'primary.main', borderWidth: 2 }} />
        
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} md={4}>
            <Card sx={{ height: '100%', borderRadius: 2, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
              <LazyImage
                src={baziImg}
                alt={language === 'chinese' ? "八字解读" : "Bazi Reading"}
                sx={{ height: 180 }}
              />
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {language === 'chinese' ? "八字命盘专题" : "Bazi Chart Analysis"}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {language === 'chinese' 
                    ? "深入了解八字命盘的构成、解读方法和应用，掌握分析命盘的基础技能。"
                    : "Learn about the structure, interpretation methods, and applications of Bazi charts, mastering the basic skills of chart analysis."}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={4}>
            <Card sx={{ height: '100%', borderRadius: 2, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
              <LazyImage
                src={amethystImg}
                alt={language === 'chinese' ? "水晶指南" : "Crystal Guide"}
                sx={{ height: 180 }}
              />
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {language === 'chinese' ? "水晶疗愈专题" : "Crystal Healing Guide"}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {language === 'chinese'
                    ? "探索各类水晶的疗愈特性，学习如何利用水晶能量改善身心健康和生活质量。"
                    : "Explore the healing properties of various crystals and learn how to use crystal energy to improve mental, physical health and quality of life."}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={4}>
            <Card sx={{ height: '100%', borderRadius: 2, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
              <LazyImage
                src={yijingImg}
                alt={language === 'chinese' ? "易经八卦" : "I Ching & Bagua"}
                sx={{ height: 180 }}
              />
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {language === 'chinese' ? "易经八卦专题" : "I Ching & Bagua Guide"}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {language === 'chinese'
                    ? "深入解析易经八卦的哲学智慧，学习六十四卦的含义与应用，探索生活中的阴阳平衡之道。"
                    : "Analyze the philosophical wisdom of I Ching and Bagua, learn the meaning and application of the 64 hexagrams, and explore the way of yin-yang balance in daily life."}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
      
      {/* 订阅提示 */}
      <Paper sx={{ p: 4, mt: 6, borderRadius: 2, backgroundColor: 'rgba(156, 39, 176, 0.05)' }}>
        <Typography variant="h5" align="center" gutterBottom>
          {language === 'chinese' ? "订阅最新文章" : "Subscribe to Latest Articles"}
        </Typography>
        <Typography variant="body1" align="center" sx={{ mb: 3 }}>
          {language === 'chinese' 
            ? "定期获取命理解读和水晶能量的专业知识，助您更好地平衡生活能量"
            : "Regularly receive professional knowledge on divination and crystal energy to help you better balance your life energy"}
        </Typography>
        <Box sx={{ textAlign: 'center' }}>
          <Link
            component={RouterLink}
            to="/contact"
            underline="none"
            sx={{
              display: 'inline-block',
              bgcolor: 'primary.main',
              color: 'white',
              py: 1,
              px: 3,
              borderRadius: 2,
              fontWeight: 'medium',
              '&:hover': {
                bgcolor: 'primary.dark',
              }
            }}
          >
            {language === 'chinese' ? "联系我们" : "Contact Us"}
          </Link>
        </Box>
      </Paper>
    </Container>
  );
};

export default Blog;